import { parse, format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { dateLocale } from '@/lib/dateLocale'
import type { Appointment, Client } from '@/lib/api'
import { AppointmentEvent } from './AppointmentEvent'
import { TimeGrid } from './TimeGrid'
import { assignColumns, formatTime } from './calendarHelpers'
import { eventColor } from './constants'
import { useLongPress } from './useLongPress'

export interface DayViewProps {
  /** 当前日期 */
  date: Date
  /** 当日预约 */
  appointments: Appointment[]
  /** 学员映射 */
  clientsMap: Map<number, Client>
  /** 是否移动端 */
  isMobile?: boolean
  /** 滑动手势（移动端切换日期） */
  swipeHandlers?: {
    onTouchStart: (e: React.TouchEvent) => void
    onTouchEnd: (e: React.TouchEvent) => void
  }
  onEventClick: (appt: Appointment) => void
  onSlotClick: (date: Date, time: string) => void
  onEventDrop?: (appt: Appointment, date: Date, time: string) => void
}

export function DayView({
  date,
  appointments,
  clientsMap,
  isMobile = false,
  swipeHandlers,
  onEventClick,
  onSlotClick,
  onEventDrop,
}: DayViewProps) {
  const { i18n } = useTranslation()
  const locale = dateLocale(i18n.language)
  const longPress = useLongPress({
    onLongPress: (pos) => {
      const el = document.elementFromPoint(pos.x, pos.y) as HTMLElement | null
      if (!el) return
      const slot = el.closest<HTMLElement>('[data-minutes]')
      const container = el.closest<HTMLElement>('[data-date]')
      const dateAttr = container?.dataset.date
      if (!slot || !dateAttr) return
      const minutes = Number(slot.dataset.minutes)
      if (Number.isNaN(minutes)) return
      onSlotClick(parse(dateAttr, 'yyyy-MM-dd', new Date()), formatTime(minutes))
    },
  })

  const positioned = assignColumns(appointments)

  const header = (
    <div className="text-center">
      <div className="text-xs text-slate-500">{format(date, 'EEEE', { locale })}</div>
      <div className="text-base">{format(date, 'MM-dd')}</div>
    </div>
  )

  return (
    <div
      className="flex flex-col h-full"
      data-testid="day-view"
      {...(swipeHandlers || {})}
    >
      <TimeGrid
        date={date}
        header={header}
        showTimeLabels
        onSlotClick={onSlotClick}
        onDrop={
          onEventDrop
            ? (d, t, payload) => {
                if (payload && typeof payload === 'object' && 'id' in payload) {
                  onEventDrop(payload as Appointment, d, t)
                }
              }
            : undefined
        }
        longPressHandlers={isMobile ? longPress : undefined}
      >
        {positioned.map((appt) => {
          const widthPct = 100 / appt.colCount
          const leftPct = appt.col * widthPct
          return (
            <AppointmentEvent
              key={appt.id}
              appt={appt}
              client={appt.clientId ? clientsMap.get(appt.clientId) : undefined}
              color={eventColor(appt)}
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
              }}
              draggable={!isMobile && appt.status === 'scheduled'}
              onClick={onEventClick}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', String(appt.id))
                e.dataTransfer.setData('application/json', JSON.stringify({
                  id: appt.id,
                  scheduledDate: appt.scheduledDate,
                  startTime: appt.startTime,
                  endTime: appt.endTime,
                }))
              }}
            />
          )
        })}
      </TimeGrid>
    </div>
  )
}
